import { Inject, Injectable } from '@angular/core';
import { AppState } from '@api-ui-app/src/app/app.state';
import { storeLimits } from '@api-ui-app/src/app/e-commerce-product-expansion/constants/store-variants-limits';
import {
  mockupStudioCreateAsset,
  mockupStudioDeleteAsset,
} from '@api-ui-app/src/app/mockup-studio/+state/mockup-studio.actions';
import * as assetsActions from '@api-ui-app/src/app/ngrx/assets.actions';
import { KeycloakAuthService } from '@api-ui-app/src/app/shared/services/keycloak-auth.service';
import { SubscriptionsFacade } from '@api-ui-app/src/app/subscriptions/+state/subscriptions.facade';
import { getAppCrownPremiumLevel } from '@api-ui-app/src/app/subscriptions/lib/helpers/visited-apps-local-storage-manager';
import { PremiumIconCrownedFeatures } from '@api-ui-app/src/app/subscriptions/types/icon-crowned-features.enum';
import { environment } from '@api-ui-app/src/environments/environment';
import { logEvent } from '@gelato-api-ui/core/analytics/helpers/trackEvent';
import { ChameleonUserIdentification } from '@gelato-api-ui/core/chameleon/chameleon-user-identification';
import { ChameleonFacade } from '@gelato-api-ui/core/chameleon/services/chameleon.facade';
import { designEditorBackgroundColor } from '@gelato-api-ui/core/design-editor/constants/desgin-editor-background-color';
import {
  EMBROIDERY_MAX_COLOR_COUNT,
  EMBROIDERY_MAX_STITCH_COUNT,
  EMBROIDERY_MIN_FONT_SIZE,
} from '@gelato-api-ui/core/design-editor/constants/desgin-editor-validation-config';
import { DesignEditorPreviewSettingsService } from '@gelato-api-ui/core/design-editor/services/design-editor-preview-settings.service';
import { DesignEditorZoomService } from '@gelato-api-ui/core/design-editor/services/design-editor-zoom.service';
import { DesignPreviewShutterstockService } from '@gelato-api-ui/core/design-editor/services/design-preview-shutterstock.service';
import { DesignEditorPlugin } from '@gelato-api-ui/core/design-editor/types/design-editor-plugin.enum';
import { DesignEditorPreviewSettings } from '@gelato-api-ui/core/design-editor/types/design-editor-preview-settings';
import { DesignEditorTabName } from '@gelato-api-ui/core/design-editor/types/design-editor-tab-name.enum';
import { EditorInboundEventType } from '@gelato-api-ui/core/design-editor/types/editor-inbound-event-type.enum';
import { EditorOutboundEventType } from '@gelato-api-ui/core/design-editor/types/editor-outbound-event-type.enum';
import { WarningType } from '@gelato-api-ui/core/design-editor/types/editor-warnings-type.enum';
import {
  PluginDataState,
  SetPluginsEventDataItem,
} from '@gelato-api-ui/core/design-editor/types/set-plugins-event-data-item';
import { DesignRendersLoaderService } from '@gelato-api-ui/core/design-renders-loader/services/design-renders-loader.service';
import { AssetProviderUid } from '@gelato-api-ui/core/designs/asset-provider-uid.enum';
import { AssetsCollection } from '@gelato-api-ui/core/designs/assets-collection';
import { DesignStructure } from '@gelato-api-ui/core/designs/design-structure';
import { getUsedMediaToTrack } from '@gelato-api-ui/core/designs/helpers/getUsedMediaToTrack';
import { isEmptyDesignStructure } from '@gelato-api-ui/core/designs/helpers/isEmptyDesignStructure';
import { EStoreFeature } from '@gelato-api-ui/core/e-commerce/e-store-feature.enum';
import { EStoreType } from '@gelato-api-ui/core/e-commerce/e-store-type.enum';
import { ECommerceStoreFeaturesService } from '@gelato-api-ui/core/e-commerce/services/e-commerce-store-features.service';
import { PreflightApiService } from '@gelato-api-ui/core/preflight/services/preflight-api.service';
import { PreflightCdnService } from '@gelato-api-ui/core/preflight/services/preflight-cdn.service';
import { ProductTypeUid } from '@gelato-api-ui/core/product-catalogue/product-type-uid.enum';
import { ApparelProductTypeUids } from '@gelato-api-ui/core/product-catalogue/product-type-uids';
import { isMirroringSupportedByProductTypeUid } from '@gelato-api-ui/core/sanity/helpers/isMirroringSupported';
import { ShutterstockImage } from '@gelato-api-ui/core/shutterstock/shutterstock-image';
import { WINDOW } from '@gelato-api-ui/core/window/window';
import { defaultDesignEditorTabsVisibilityConfig } from '@gelato-api-ui/design-editor/constants/default-design-editor-tabs-visibility-config';
import { DesignEditorApplicationsService } from '@gelato-api-ui/design-editor/services/design-editor-applications.service';
import { DesignEditorMode } from '@gelato-api-ui/design-editor/types/design-editor-mode.enum';
import { SetupEditorConfig } from '@gelato-api-ui/design-editor/types/setup-editor-config';
import { FeatureSwitcherService } from '@gelato-api-ui/sdk/src/lib/feature-switcher/feature-switcher.service';
import { FeatureFlagEnum } from '@gelato-api-ui/sdk/src/lib/feature-switcher/featureFlagEnum';
import { FileUploaderConfig } from '@gelatoas/file-uploader';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Observable, throwError } from 'rxjs';
import { catchError, filter, first, take, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class DesignEditorCommunicationService {
  protected designEditorUrl = environment.microFrontendServiceUrls.designEditor;
  protected editorReady$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  protected defaultIFrameId = 'printFileEditor';
  protected iFrameId = this.defaultIFrameId;
  protected mode: DesignEditorMode = null;
  protected storeType: EStoreType = null;

  private tabsVisibility = defaultDesignEditorTabsVisibilityConfig;

  constructor(
    private readonly store: Store<AppState>,
    private readonly translateService: TranslateService,
    private readonly designRendersLoaderService: DesignRendersLoaderService,
    private readonly eCommerceStoreFeaturesService: ECommerceStoreFeaturesService,
    private readonly designEditorPreviewSettingsService: DesignEditorPreviewSettingsService,
    private readonly subscriptionsFacade: SubscriptionsFacade,
    private readonly chameleonFacade: ChameleonFacade,
    private readonly auth: KeycloakAuthService,
    private readonly featureSwitcherService: FeatureSwitcherService,
    private readonly designEditorApplicationsService: DesignEditorApplicationsService,
    private readonly designEditorZoomService: DesignEditorZoomService,
    private readonly designPreviewShutterstockService: DesignPreviewShutterstockService,
    private readonly preflightApiService: PreflightApiService,
    private readonly preflightCdnService: PreflightCdnService,
    @Inject(WINDOW) private readonly window: Window,
  ) {
    this.window.addEventListener('message', (messageEvent: MessageEvent) => {
      const payload = messageEvent.data?.data;

      switch (messageEvent.data?.type) {
        case EditorOutboundEventType.ready:
          this.editorReady$.next(true);
          break;
        case EditorOutboundEventType.assetUploaded:
          switch (this.mode) {
            case DesignEditorMode.MOCKUP:
              this.store.dispatch(mockupStudioCreateAsset({ fileUrl: payload?.url, id: payload?.file?.id }));
              break;
            default:
              this.store.dispatch(new assetsActions.ProcessUploadSuccessPayload(payload));
              break;
          }
          break;
        case EditorOutboundEventType.removeFile:
          switch (this.mode) {
            case DesignEditorMode.MOCKUP:
              this.store.dispatch(mockupStudioDeleteAsset({ id: payload.imageId }));
              break;
            default:
              this.store.dispatch(new assetsActions.RemoveAsset(payload.imageId));
              break;
          }
          break;
        default:
          break;
      }
    });
  }

  setupEditor(config: SetupEditorConfig) {
    this.setupPlugins(config);
    const { productTypeUid, hasEmbroidery } = config;

    this.sendPostMessage(EditorInboundEventType.setDefaultZoom, {
      zoom: this.designEditorZoomService.getDefaultZoom(productTypeUid),
    });

    const isWallpaper =
      productTypeUid === ProductTypeUid.WALLPAPER || productTypeUid === ProductTypeUid.WALLPAPER_SAMPLE;

    const isProductTabSupported = this.isProductTabSupportedByProductTypeUid(productTypeUid);

    if (isProductTabSupported) {
      this.tabsVisibility[DesignEditorTabName.PRODUCT] = true;
    }

    this.tabsVisibility[DesignEditorTabName.LAYERS] = !isWallpaper;
    this.tabsVisibility[DesignEditorTabName.TEXTS] = !isWallpaper;
    this.tabsVisibility[DesignEditorTabName.LAYOUTS] = !isWallpaper && !hasEmbroidery;
    this.tabsVisibility[DesignEditorTabName.SHAPES] =
      !hasEmbroidery && !isWallpaper && this.mode !== DesignEditorMode.PRODUCT_EXPANSION_PRODUCT_EDITING;

    this.setupSidebarTabs();

    this.sendPostMessage(
      EditorInboundEventType.setSidebarActiveTabName,
      isProductTabSupported ? DesignEditorTabName.PRODUCT : DesignEditorTabName.GALLERY,
    );

    this.setupEditorHostSettings(config, isWallpaper);
  }

  setupEditorByDesignStructure(designStructure: DesignStructure, productTypeUid: ProductTypeUid) {
    this.preflightApiService
      .getProductResolution({ product_uid: designStructure.product_uid })
      .pipe(take(1))
      .subscribe(dpiResolution => {
        this.sendPostMessage(EditorInboundEventType.setEditorHostSettings, {
          dpiLevels: {
            low: dpiResolution.dpi.min,
            medium: dpiResolution.dpi.ok,
          },
        });
      });

    if (ApparelProductTypeUids.includes(productTypeUid)) {
      this.sendPostMessage(EditorInboundEventType.setSpreadsDefaultZoom, {
        zoom: this.designEditorZoomService.getSpreadsDefaultZoom(designStructure.spreads),
      });
    }

    const dimensions = designStructure.related_dimensions || designStructure.dimensions;
    if ([ProductTypeUid.WALLPAPER, ProductTypeUid.WALLPAPER_SAMPLE].includes(productTypeUid) && dimensions) {
      this.sendPostMessage(EditorInboundEventType.setImagePatternScaleBases, [
        { value: dimensions.min_width, dimension: 'width', label: 'Fixed to roll size' },
      ]);
    }
  }

  toggleTab(tab: DesignEditorTabName, visible: boolean) {
    if (this.tabsVisibility[tab] === visible) {
      return;
    }

    this.tabsVisibility[tab] = visible;
    this.setupSidebarTabs();
  }

  toggleShutterstockTab(isTabVisible: boolean) {
    this.subscriptionsFacade.isPaidSubscriptionActive$
      .pipe(
        take(1),
        tap(isGelatoPlusOrGoldActive => {
          const state = isTabVisible ? PluginDataState.enabled : PluginDataState.not_visible;
          this.setShutterstockPlugin(state, isGelatoPlusOrGoldActive);
        }),
      )
      .subscribe();
  }

  isEditorReady(): Observable<boolean> {
    return this.editorReady$;
  }

  getDesignEditorUrl(): string {
    return this.designEditorUrl;
  }

  initInstance(
    mode: DesignEditorMode,
    fileUploaderConfig,
    storeType: EStoreType = null,
    iFrameId: string = this.defaultIFrameId,
  ) {
    this.iFrameId = iFrameId;
    this.mode = mode;
    this.storeType = storeType;
    this.tabsVisibility = defaultDesignEditorTabsVisibilityConfig;

    if (storeType) {
      this.sendPostMessage(EditorInboundEventType.variantsSetLimit, storeLimits[storeType]);
    }

    this.setupEditorOnReady(fileUploaderConfig);
  }

  editorClosed() {
    this.editorReady$.next(false);
  }

  sendPostMessage(type: EditorInboundEventType | string, data: any = null, plugin?: string) {
    this.editorReady$
      .pipe(
        filter(editorReady => Boolean(editorReady)),
        take(1),
        tap(() => {
          const frame: any = document.getElementById(this.iFrameId);

          if (!frame || !frame.contentWindow) {
            return;
          }

          frame.contentWindow.postMessage({ type, data, plugin }, this.designEditorUrl);
        }),
      )
      .subscribe();
  }

  sendDesignData(designData: DesignStructure) {
    logEvent('sendDesignDataToEditor', {
      productUidSentToEditor: designData.product_uid,
      isEmptyDesign: isEmptyDesignStructure(designData),
      designMediaInfo: getUsedMediaToTrack(designData),
    });
    this.sendPostMessage(EditorInboundEventType.setDesignData, designData);
    this.requestDesignData();
  }

  requestDesignData() {
    this.sendPostMessage(EditorInboundEventType.requestDesignData, {
      checkWarnings: [
        {
          type: 'LOW_DPI',
          level: ['MEDIUM', 'HIGH'],
        },
      ],
    });
  }

  importImageFromShutterstock(assetProviderUid: AssetProviderUid, image: ShutterstockImage) {
    this.store.dispatch(new assetsActions.ImportImageFromShutterstock(assetProviderUid, image));
    this.sendPostMessage(EditorInboundEventType.setSidebarActiveTabName, DesignEditorTabName.GALLERY);
  }

  closeShutterStockGallery() {
    this.sendPostMessage(EditorInboundEventType.setSidebarActiveTab, -1);
  }

  async loadDesignRenders(
    designData: DesignStructure,
    assets: AssetsCollection,
    removeSamples: boolean,
    productTypeUid: ProductTypeUid,
  ) {
    const isShutterstockUsed = this.designPreviewShutterstockService.isDesignHasShutterstockAssets(assets, designData);
    const previewSettings: DesignEditorPreviewSettings = await this.designEditorPreviewSettingsService
      .fetch(designData?.product_uid, isShutterstockUsed)
      .toPromise();

    const isShutterstockFlatPreviewNeeded = this.designPreviewShutterstockService.isShutterstockFlatPreviewSupported(
      productTypeUid,
      previewSettings?.noFlatPreviews,
      isShutterstockUsed,
    );

    if (previewSettings) {
      this.sendPostMessage(
        EditorInboundEventType.setEditorHostSettings,
        this.designEditorPreviewSettingsService.mapToSetEditorPreviewSettingsPostMessagePayload(previewSettings),
      );
      if (previewSettings.clientSideRendering) {
        return;
      }

      const renders = await this.designRendersLoaderService.getDesignRenders(
        designData,
        this.designEditorPreviewSettingsService.mapToDesignRendersLoadingSettings(previewSettings),
        removeSamples,
        ApparelProductTypeUids.includes(productTypeUid),
        isShutterstockFlatPreviewNeeded,
      );
      let previews = { flat: [], the3d: renders?.the3d };
      if (renders?.flat) {
        previews.flat = await Promise.all(
          renders.flat.map((promise, index) =>
            promise
              .then(preview => {
                this.sendPostMessage(EditorInboundEventType.flatPreviewReady, { index, preview });
                return preview;
              })
              .catch(() => {
                this.sendPostMessage(EditorInboundEventType.flatPreviewFailed, { index });
              }),
          ),
        );
      }

      previews.flat = previews.flat.filter(preview => !!preview);

      if (previews.flat.length || previews.the3d) {
        this.sendPostMessage(EditorInboundEventType.setRenders, previews);
      } else {
        this.sendPostMessage(EditorInboundEventType.rendersError);
      }
    } else {
      this.sendPostMessage(EditorInboundEventType.rendersError);
    }
  }

  async requestAvailablePreviews(productUid: string) {
    const previews = await this.designRendersLoaderService.getAvailablePreviews(productUid);

    if (previews) {
      this.sendPostMessage(EditorInboundEventType.setFlatPreviewAvailable, previews && previews.flat);
      this.sendPostMessage(EditorInboundEventType.setThe3dPreviewAvailable, previews && previews.the3d);
    }
  }

  private setupEditorHostSettings({ productTypeUid, hasEmbroidery }: SetupEditorConfig, isWallpaper: boolean) {
    let hostSettings: { [key: string]: any } = {
      enableMirroring: isMirroringSupportedByProductTypeUid(productTypeUid),
      showDoubleLine: ApparelProductTypeUids.includes(productTypeUid),
      allowSpreadBackgroundColorChange: !hasEmbroidery,
      digitizeDesignElements: hasEmbroidery,
    };

    if (isWallpaper) {
      hostSettings = {
        ...hostSettings,
        ...{
          allowAddElements: false,
          allowRemoveElements: false,
          allowLockElements: false,
          activateWallpaperEmptyScreen: true,
          allowShowRollerStripesChange: true,
          allowShowRulersChange: true,
          resizeProductBasedOnFirstElement: true,
          enableImagePatterns: true,
        },
      };
    }

    if (productTypeUid === ProductTypeUid.PHONE_CASE) {
      hostSettings.showPrintAreaLines = false;
    }

    const enabledWarnings = {
      [WarningType.LowDPI]: true,
      [WarningType.NotVisible]: true,
      [WarningType.OutOfRange]: true,
      [WarningType.MaxStitchCount]: false,
      [WarningType.MaxColorCount]: false,
    };

    if (hasEmbroidery) {
      hostSettings.minFontSize = EMBROIDERY_MIN_FONT_SIZE;
      hostSettings.digitizationRules = {
        maxColorsCount: EMBROIDERY_MAX_COLOR_COUNT,
        maxStitchCount: EMBROIDERY_MAX_STITCH_COUNT,
      };

      hostSettings.allowPersonalizationLock = false;

      enabledWarnings[WarningType.MaxStitchCount] = true;
      enabledWarnings[WarningType.MaxColorCount] = true;
      hostSettings.enabledWarnings = enabledWarnings;
      this.preflightCdnService
        .getEmbroideryPalette()
        .pipe(take(1))
        .subscribe(allowedColors => {
          this.sendPostMessage(EditorInboundEventType.setEditorHostSettings, {
            colorPickerConfig: {
              enableOpacity: false,
              allowedColors,
              noColor: { label: 'Remove thread color' },
            },
          });
        });
    } else {
      hostSettings = {
        ...hostSettings,
        enabledWarnings,
        ...{
          colorPickerConfig: {
            enableOpacity: false,
          },
          minFontSize: 1,
        },
      };
    }

    this.sendPostMessage(EditorInboundEventType.setEditorHostSettings, hostSettings);
  }

  private setupEditorOnReady(fileUploaderConfig: FileUploaderConfig) {
    this.sendPostMessage(EditorInboundEventType.setLocale, this.translateService.currentLang);
    this.sendPostMessage(EditorInboundEventType.setDefaultTextStyle, {
      bold: false,
      italic: false,
      underline: false,
      stroked: false,
      fontFamily: 'OpenSans-Regular',
      fontSize: 48,
      color: '#000000',
      lineHeight: 1,
      textAlign: 'center',
      textDirection: '',
      angle: 0,
    });

    const isMockup = this.mode === DesignEditorMode.MOCKUP;

    this.featureSwitcherService
      .isFeatureEnabledForCurrentUser(FeatureFlagEnum.enable_mockup_leaning)
      .pipe(take(1))
      .subscribe(enable_mockup_leaning => {
        this.sendPostMessage(EditorInboundEventType.setEditorHostSettings, {
          enableMockupLeaning: enable_mockup_leaning && isMockup,
        });
      });

    this.sendPostMessage(EditorInboundEventType.setEditorHostSettings, {
      allowUseNotUploadedImages: true,
      showDesignScroll: true,
      showCanvasOperationHelper: !isMockup,
      backgroundColor: designEditorBackgroundColor,
      enableImageShadow: isMockup,
      userInterface: {
        fixedMobileBottomBar: true,
      },
      showEmptyDesignPlaceholder: true,
    });
    this.sendPostMessage(EditorInboundEventType.setTrackDesignChanges, true);

    if (
      [
        DesignEditorMode.PRODUCT_CREATION,
        DesignEditorMode.PRODUCT_EDITING,
        DesignEditorMode.PRODUCT_EXPANSION_PRODUCT_EDITING,
      ].includes(this.mode)
    ) {
      this.sendPostMessage(EditorInboundEventType.setAsVariantFlow, true);
      this.tabsVisibility[DesignEditorTabName.VARIANTS] = true;
    }

    if (this.isECommerceMode()) {
      const isDesignPersonalizationSupported = [
        EStoreFeature.DESIGN_PERSONALIZATION,
        EStoreFeature.ADVANCED_DESIGN_PERSONALIZATION_EMAIL,
      ].some((feature: EStoreFeature): boolean =>
        this.eCommerceStoreFeaturesService.isFeatureSupported(this.storeType, feature),
      );

      if (isDesignPersonalizationSupported) {
        this.sendPostMessage(EditorInboundEventType.setEditorHostSettings, {
          allowLockElements: true,
          allowPersonalizationLock: true,
          allowSampleFlagChange: true,
          allowStaticFlagChange: true,
        });
        this.sendPostMessage(EditorInboundEventType.setDefaultPersonalizationLock, true);
      }
    }

    this.tabsVisibility[DesignEditorTabName.MOCKUP] = isMockup;
    this.tabsVisibility[DesignEditorTabName.SCENE] = isMockup;
    this.tabsVisibility[DesignEditorTabName.PLACEHOLDER] = isMockup;
    this.tabsVisibility[DesignEditorTabName.LAYOUTS] = this.isLayoutsTabSupportedByMode();
    this.tabsVisibility[DesignEditorTabName.PRODUCT] = this.isProductTabSupportedByMode();

    this.sendPostMessage(EditorInboundEventType.setUploaderConfig, fileUploaderConfig);

    this.setupChameleon();
    this.setupAmplitude();
    this.setupSidebarTabs();

    combineLatest([
      this.featureSwitcherService.isFeatureEnabledForCurrentUser(FeatureFlagEnum.destygo_webchat),
      this.featureSwitcherService.isFeatureEnabledForCurrentUser(FeatureFlagEnum.editor_image_edit_tab),
    ])
      .pipe(take(1))
      .subscribe(([isWebChatEnabled, editImageFlag]) => {
        this.sendPostMessage(EditorInboundEventType.setEditorHostSettings, {
          enableHelperCenter: { chatBot: isWebChatEnabled, feedback: false },
          editImageFlag,
        });
      });
  }

  requestQuantizedAsset(assetId: string) {
    this.store.dispatch(assetsActions.quantizeAsset({ assetId }));
  }

  requestDigitizedAsset(requestData: {
    elementType: 'text' | 'image' | 'addon';
    requestVersion: number;
    elementId: number;
    imageId: string | undefined;
    payload: any;
  }) {
    const { elementType, requestVersion, elementId, imageId: sourceImageId, payload } = requestData;

    let digitizedAsset;
    if (elementType === 'text') {
      digitizedAsset = this.requestDigitizedText(payload, sourceImageId);
    } else {
      digitizedAsset = this.requestDigitizedImage(payload, sourceImageId);
    }

    digitizedAsset.pipe(take(1)).subscribe(
      ({ url, id: imageId, margin = 0, used_colors: usedColors, stitch_count: stitchCount }) => {
        this.sendPostMessage(EditorInboundEventType.setDigitizedAsset, {
          requestVersion,
          url,
          elementId,
          imageId,
          usedColors,
          stitchCount,
          margin,
          payload,
        });
      },
      error =>
        this.sendPostMessage(EditorInboundEventType.setDigitizedAssetError, {
          requestVersion,
          elementId,
          error,
        }),
    );
  }

  private requestDigitizedText(payload: any, imageId: string) {
    return this.preflightApiService
      .getDigitizedText(payload, imageId)
      .pipe(
        catchError(err => {
          return throwError(err);
        }),
      )
      .pipe(first());
  }

  private requestDigitizedImage(payload: any, imageId: string) {
    return this.preflightApiService
      .getDigitizedImage(payload, imageId)
      .pipe(
        catchError(err => {
          return throwError(err);
        }),
      )
      .pipe(first());
  }

  private isECommerceMode() {
    return [
      DesignEditorMode.PRODUCT_CREATION,
      DesignEditorMode.PRODUCT_EDITING,
      DesignEditorMode.PRODUCT_VARIANT_CONNECTION,
      DesignEditorMode.PRODUCT_VARIANT_EDITING,
    ].includes(this.mode);
  }

  private setupAmplitude() {
    this.auth
      .userId()
      .pipe(
        take(1),
        tap(userId => this.sendPostMessage(EditorInboundEventType.setupAmplitude, { userId })),
      )
      .subscribe();
  }

  private setupPlugins(config: SetupEditorConfig) {
    this.designEditorApplicationsService
      .setupEditorPlugins(config, this.mode)
      .pipe(
        tap((data: SetPluginsEventDataItem[]) => {
          this.setPlugins(data);
        }),
      )
      .subscribe();
  }

  private setupSidebarTabs() {
    const tabs = [
      DesignEditorTabName.MOCKUP,
      DesignEditorTabName.SCENE,
      DesignEditorTabName.PLACEHOLDER,
      DesignEditorTabName.PRODUCT,
      DesignEditorTabName.VARIANTS,
      DesignEditorTabName.LAYERS,
      DesignEditorTabName.GALLERY,
      DesignEditorTabName.TEXTS,
      DesignEditorTabName.SHAPES,
      DesignEditorTabName.TEMPLATES,
      DesignEditorTabName.WARNINGS,
      DesignEditorTabName.LAYOUTS,
      DesignEditorTabName.SETTINGS,
    ].filter(tab => this.tabsVisibility[tab]);

    this.sendPostMessage(EditorInboundEventType.setSidebarTabs, tabs);
  }

  private setShutterstockPlugin(state: PluginDataState, isGelatoPlusOrGoldActive: boolean) {
    this.setPlugins([
      {
        state,
        name: DesignEditorPlugin.APP_SHUTTERSTOCK,
        indexInTabs: 5,
        extra: {
          premiumLevel: getAppCrownPremiumLevel(PremiumIconCrownedFeatures.SHUTTERSTOCK, isGelatoPlusOrGoldActive),
        },
      },
    ]);
  }

  setPlugins(data: SetPluginsEventDataItem[]) {
    this.sendPostMessage(EditorInboundEventType.setPlugins, data);
  }

  private setupChameleon() {
    this.chameleonFacade.identify$
      .pipe(
        filter((identify: ChameleonUserIdentification) => Boolean(identify && identify.userId && identify.data)),
        take(1),
        tap((identify: ChameleonUserIdentification) => {
          this.sendPostMessage(EditorInboundEventType.setupChameleon, {
            token: this.chameleonFacade.getAccountToken(),
            identify,
          });
        }),
      )
      .subscribe();
  }

  private isProductTabSupportedByMode(): boolean {
    return ![DesignEditorMode.MOCKUP, DesignEditorMode.PRODUCT_VARIANT_EDITING].includes(this.mode);
  }

  // the editor has custom controls in the product tab for calendars and canvases
  private isProductTabSupportedByProductTypeUid(productTypeUid: ProductTypeUid): boolean {
    return (
      this.mode !== DesignEditorMode.MOCKUP &&
      (this.mode !== DesignEditorMode.PRODUCT_VARIANT_EDITING ||
        productTypeUid === ProductTypeUid.CALENDAR ||
        productTypeUid === ProductTypeUid.CANVAS)
    );
  }

  private isLayoutsTabSupportedByMode() {
    return ![DesignEditorMode.MOCKUP, DesignEditorMode.PRODUCT_EXPANSION_PRODUCT_EDITING].includes(this.mode);
  }
}
